import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ClientRoute from './components/ClientRoute';
import HomePage from './pages/HomePage';
import ProviderPage from './pages/ProviderPage';
import LoginPage from './pages/LoginPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import AdminLoginPage from './pages/AdminLoginPage';
import SignupProviderPage from './pages/SignupProviderPage';
import DashboardHomePage from './pages/DashboardHomePage';
import ServicesPage from './pages/ServicesPage';
import AvailabilityPage from './pages/AvailabilityPage';
import AllBookingsPage from './pages/AllBookingsPage';
import AdminPage from './pages/AdminPage';
import AdminSystemPage from './pages/AdminSystemPage';
import AdminPromoCodesPage from './pages/AdminPromoCodesPage';
import ReviewPage from './pages/ReviewPage';
import DashboardReviewsPage from './pages/DashboardReviewsPage';
import RouteZonesPage from './pages/RouteZonesPage';
import InvitesPage from './pages/InvitesPage';
import ReferralsPage from './pages/ReferralsPage';
import SettingsPage from './pages/SettingsPage';
import EmergencyPage from './pages/EmergencyPage';
import EmergencyFinderPage from './pages/EmergencyFinderPage';
import CalendarPage from './pages/CalendarPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import OnboardingPage from './pages/OnboardingPage';
// Consultation pages
import ConsultationSettingsPage from './pages/ConsultationSettingsPage';
import ConsultationsPage from './pages/ConsultationsPage';
import BookConsultationPage from './pages/BookConsultationPage';
import MyConsultationsPage from './pages/MyConsultationsPage';
// Community
import LoungePage from './pages/LoungePage';
import AdminCommunityPage from './pages/AdminCommunityPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminClientDetailPage from './pages/AdminClientDetailPage';
// Client
import ClientSignupPage from './pages/ClientSignupPage';
import ClientDashboardPage from './pages/ClientDashboardPage';
import ClientBookingsPage from './pages/ClientBookingsPage';
import ClientBookingDetailPage from './pages/ClientBookingDetailPage';
import ClientAnimalsPage from './pages/ClientAnimalsPage';
import ClientProvidersPage from './pages/ClientProvidersPage';
import ClientInvoicesPage from './pages/ClientInvoicesPage';
// Field View (provider mobile workflow)
import FieldViewPage from './pages/FieldViewPage';
// End-of-Day Closeout
import CloseoutPage from './pages/CloseoutPage';
// Weekly Review, Approval & PDF Report (RW-BUILD-001)
import WeeklyReviewPage from './pages/WeeklyReviewPage';
// Invoices (Session 32)
import InvoiceSettingsPage from './pages/InvoiceSettingsPage';
import PublicInvoicePage from './pages/PublicInvoicePage';
// Voice Notes & Reminders (RW-BUILD-005)
import VoiceNotesPage from './pages/VoiceNotesPage';
import RemindersPage from './pages/RemindersPage';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/providers/:slug" element={<ProviderPage />} />
          <Route path="/provider/:slug" element={<ProviderPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/google" element={<GoogleCallbackPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/provider/signup" element={<SignupProviderPage />} />
          <Route path="/signup-provider" element={<SignupProviderPage />} />
          <Route path="/emergency" element={<EmergencyPage />} />
          <Route path="/emergency-finder" element={<EmergencyFinderPage />} />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route path="/review/:bookingId" element={<ReviewPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardHomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/services"
            element={
              <ProtectedRoute>
                <ServicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/availability"
            element={
              <ProtectedRoute>
                <AvailabilityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/bookings"
            element={
              <ProtectedRoute>
                <AllBookingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reviews"
            element={
              <ProtectedRoute>
                <DashboardReviewsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/route-zones"
            element={
              <ProtectedRoute>
                <RouteZonesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/invites"
            element={
              <ProtectedRoute>
                <InvitesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/referrals"
            element={
              <ProtectedRoute>
                <ReferralsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/invoice-settings"
            element={
              <ProtectedRoute>
                <InvoiceSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/invoice/:token" element={<PublicInvoicePage />} />
          <Route
            path="/dashboard/calendar"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/clients"
            element={
              <ProtectedRoute>
                <ClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/clients/:clientId"
            element={
              <ProtectedRoute>
                <ClientDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/system"
            element={
              <AdminRoute>
                <AdminSystemPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/promo-codes"
            element={
              <AdminRoute>
                <AdminPromoCodesPage />
              </AdminRoute>
            }
          />
          {/* Consultation Routes */}
          <Route
            path="/dashboard/consultation-settings"
            element={
              <ProtectedRoute>
                <ConsultationSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/consultations"
            element={
              <ProtectedRoute>
                <ConsultationsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/provider/:slug/consultation" element={<BookConsultationPage />} />
          <Route path="/providers/:slug/consultation" element={<BookConsultationPage />} />
          <Route
            path="/my-consultations"
            element={
              <ClientRoute>
                <MyConsultationsPage />
              </ClientRoute>
            }
          />
          {/* Provider Community Routes */}
          <Route
            path="/dashboard/lounge"
            element={
              <ProtectedRoute>
                <LoungePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/community"
            element={
              <AdminRoute>
                <AdminCommunityPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <AdminRoute>
                <AdminClientsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/clients/:clientId"
            element={
              <AdminRoute>
                <AdminClientDetailPage />
              </AdminRoute>
            }
          />
          {/* Client Routes */}
          <Route path="/client/signup" element={<ClientSignupPage />} />
          <Route
            path="/client/dashboard"
            element={
              <ClientRoute>
                <ClientDashboardPage />
              </ClientRoute>
            }
          />
          <Route
            path="/client/bookings"
            element={
              <ClientRoute>
                <ClientBookingsPage />
              </ClientRoute>
            }
          />
          <Route
            path="/client/bookings/:bookingId"
            element={
              <ClientRoute>
                <ClientBookingDetailPage />
              </ClientRoute>
            }
          />
          <Route
            path="/client/animals"
            element={
              <ClientRoute>
                <ClientAnimalsPage />
              </ClientRoute>
            }
          />
          <Route
            path="/client/providers"
            element={
              <ClientRoute>
                <ClientProvidersPage />
              </ClientRoute>
            }
          />
          <Route
            path="/client/invoices"
            element={
              <ClientRoute>
                <ClientInvoicesPage />
              </ClientRoute>
            }
          />
          {/* Provider Field View (mobile working screen) */}
          <Route
            path="/dashboard/field"
            element={
              <ProtectedRoute>
                <FieldViewPage />
              </ProtectedRoute>
            }
          />
          {/* End-of-Day Closeout */}
          <Route
            path="/dashboard/closeout"
            element={
              <ProtectedRoute>
                <CloseoutPage />
              </ProtectedRoute>
            }
          />
          {/* Weekly Review, Approval & PDF Report */}
          <Route
            path="/dashboard/weekly-review"
            element={
              <ProtectedRoute>
                <WeeklyReviewPage />
              </ProtectedRoute>
            }
          />
          {/* Voice Notes & Reminders (RW-BUILD-005) */}
          <Route
            path="/dashboard/notes"
            element={
              <ProtectedRoute>
                <VoiceNotesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/reminders"
            element={
              <ProtectedRoute>
                <RemindersPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster />
      </Router>
    </AuthProvider>
  );
}

export default App;